import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { BAND_WORDS, useHeld, useReleaseHeld } from '../api/triage.ts';
import { DEPARTMENTS, type Department } from '../api/taskQueries.ts';
import { releaseDepartments, releasePreview } from '../tasks/triage.ts';
import { field, label } from '../components/contacts/Sheet.tsx';
import { Refusal, chip, go, plain } from '../components/tasks/Fields.tsx';
import { HeldCounts } from './Triage.tsx';

/**
 * Release held work (A-#144, 26 October 2026): ruling 5's band release, on its own page in the Agency menu, off the
 * task screens. Offered to whoever the server's may_release names (release_held_tasks()'s own test: tiers 1-2, and
 * since A-#149 a director for their own department); the menu reads the same text. The departments offered are the
 * server's may_release_in, and "Every department" only with may_release_all (tiers 1-2). The counts first, then the
 * act, which shows what it would make before it is confirmed.
 * Overdue work is not here: it is released through Triage, one selection at a time.
 */
export function ReleaseHeld() {
  const held = useHeld();
  const rows = held.data?.rows ?? [];
  const mayAct = held.data?.may_release === true;
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Release held work</h1>
          <p className="mt-1 max-w-[60rem] text-sm text-neutral-700">
            Deadlines from before the cutover are held: none is anyone's task until it is released. A band due within
            14 days, or later, is released here as a whole; overdue work goes through{' '}
            <Link to="/tasks/triage" className="font-semibold underline underline-offset-2">Triage</Link>.
          </p>
        </header>
        <div className="page-body px-4 pb-4 lg:px-5">
          {held.error && !held.data ? (
            <LoadFailure what="The held deadlines" error={held.error} onRetry={() => void held.refetch()} retrying={held.isFetching} />
          ) : !held.data ? (
            <p className="py-6 text-neutral-700">Loading…</p>
          ) : !mayAct ? (
            <p role="alert" className="py-6 font-semibold text-neutral-900">
              Releasing held work is open to tiers 1 and 2, and to a director for their own department.
            </p>
          ) : (
            <>
              <HeldCounts rows={rows} />
              <ReleaseAct rows={rows} all={held.data.may_release_all === true} departments={held.data.may_release_in ?? []} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Ruling 5's act: a band, for one department or (tiers 1-2) all, with the counts shown before it is confirmed. A
 * director is offered their own departments only (A-#149), and starts at the first of them; with one, it is fixed text.
 */
function ReleaseAct({ rows, all, departments }: {
  rows: Parameters<typeof releasePreview>[0]; all: boolean; departments: Department[];
}) {
  const release = useReleaseHeld();
  const offer = releaseDepartments(all, departments, DEPARTMENTS);
  const [department, setDepartment] = useState<Department | 'all'>(offer.start);
  const [band, setBand] = useState<'within_14' | 'later'>('within_14');
  const [reason, setReason] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const preview = releasePreview(rows, department === 'all' ? null : department, band);
  const reset = () => { setReviewing(false); release.reset(); };
  return (
    <section aria-labelledby="act" className="mt-5 rounded-lg border border-neutral-300 px-3 py-3">
      <h2 id="act" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">Release a band</h2>
      {release.data ? (
        <div className="mt-2 space-y-2">
          <p role="status" className="font-semibold text-neutral-900">{release.data.released} {release.data.released === 1 ? 'task' : 'tasks'} made.</p>
          <button type="button" className={plain} onClick={() => { reset(); setReason(''); }}>Release another</button>
        </div>
      ) : !reviewing ? (
        <div className="mt-2 space-y-3">
          <div role="radiogroup" aria-label="Band" className="flex flex-wrap gap-2">
            {(['within_14', 'later'] as const).map((b) => (
              <button key={b} type="button" role="radio" aria-checked={band === b} className={chip(band === b)} onClick={() => setBand(b)}>{BAND_WORDS[b]}</button>
            ))}
          </div>
          {offer.fixed ? (
            // One department and not all (a director): fixed text, as the action sheet shows its department.
            <p className="text-neutral-900"><span className={label}>Department</span>{offer.fixed}</p>
          ) : (
            <label className={label}>Department
              <select value={department} onChange={(e) => setDepartment(e.target.value as Department | 'all')} className={field}>
                {offer.every && <option value="all">Every department</option>}
                {offer.offered.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          <label className={label}>Why
            <input type="text" maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} className={field} />
          </label>
          <button type="button" className={go} disabled={!reason.trim()} onClick={() => setReviewing(true)}>Review the counts</button>
        </div>
      ) : (
        <div className="mt-2 space-y-3">
          <p className="text-neutral-900">This makes a task for each held deadline {BAND_WORDS[band].toLowerCase()}:</p>
          {preview.total === 0 ? <p className="font-semibold text-neutral-900">Nothing is held in that band.</p> : (
            <ul className="space-y-1">
              {preview.byDepartment.map((d) => (
                <li key={d.department} className="flex gap-3 text-neutral-900"><span className="w-40">{d.department}</span><span className="font-semibold tabular-nums">{d.count}</span></li>
              ))}
              <li className="flex gap-3 border-t border-neutral-300 pt-1 font-semibold text-neutral-900"><span className="w-40">In all</span><span className="tabular-nums">{preview.total}</span></li>
            </ul>
          )}
          <p className="text-sm text-neutral-700">The bands are counted again at the moment of release, so the number made can differ slightly.</p>
          <Refusal error={release.error} />
          <div className="flex flex-wrap gap-2">
            <button type="button" className={go} disabled={preview.total === 0 || release.isPending}
                    onClick={() => release.mutate({ department: department === 'all' ? null : department, band, reason: reason.trim() })}>
              Release {preview.total} as tasks
            </button>
            <button type="button" className={plain} onClick={reset}>Back</button>
          </div>
        </div>
      )}
    </section>
  );
}
